
import { NextImage } from '@/components/NextImage'
import { useTranslation } from '@/localization/useTranslation'
import { Button } from '@/components/Buttons/Button'
import { IPlaylistItem } from '@/types'
import { useImage } from '@/hooks/images/useImage'
import { useEventDate } from '@/hooks/utils/useEventDate'
import { useCinematicsStore } from '@/cinematics/stores/CinematicsStore'
import { hexToRgba } from '@/utils'

import classNames from 'classnames'
import classes from './styles.module.scss'

interface BannerCardProps {
  item: IPlaylistItem
  itemIndex: number
  handleCardClick?: (item: any, action?: { type?: 'internal' | 'external', trigger?: string, link?: string }) => void
  isPlaceholder?: boolean
}

export const BannerCard = (props: BannerCardProps) => {
  const {
    item,
    itemIndex,
    handleCardClick,
    isPlaceholder
  } = props
  const { t } = useTranslation()
  const customButton = item?.customButton
  const title = t(item?.title)
  const description = t(item?.description?.plain)
  const isPriority = itemIndex === 0
  const hasFeaturedImage = false
  const eventData = item?.__belong?.Event

  const { url: background } = useImage({ images: item?.images, cut: 'banner', scale: '100' })
  const { url: backgroundMobile } = useImage({ images: item?.images, cut: 'mediumv', scale: '50' })
  const { url: featuredImage } = useImage({ images: item?.images, cut: 'square', scale: '100' })

  const handleClick = () => {
    const { type, trigger, link } = customButton || {}

    handleCardClick?.(item, { type, trigger, link })
  }

  return (
    <div className={classNames({
      [classes.card]: true,
      [classes['card--placeholder']]: isPlaceholder
    })}>
      <NextImage
        className={classes.background}
        alt={title}
        fill={true}
        sizes="100vw"
        src={background}
        priority={isPriority}
        loading={isPriority ? 'eager' : 'lazy'}
      />
      <NextImage
        className={classes.background__mobile}
        alt={title}
        fill={true}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 20vw"
        src={backgroundMobile}
        priority={isPriority}
        loading={isPriority ? 'eager' : 'lazy'}
      />
      <div className={classes.content}>
        <div className={classes.content__information}>
          <div className={classes.content__text}>
            <h2 className={classes.title}>
              {title}
            </h2>
            <h3 className={classes.description}>
              {description}
            </h3>
          </div>
          {eventData &&
            <EventCard event={eventData} />
          }
          {customButton &&
            <div className={classes.button}>
              <Button fullWidth onClick={handleClick}>
                {t(customButton?.text)}
              </Button>
            </div>
          }
        </div>
        <div className={classes.content__image}>
          {hasFeaturedImage &&
            <NextImage
              src={featuredImage}
              alt={`${title}-featured-image`}
              width={200}
              height={200}
              priority={isPriority}
            />
          }
        </div>
      </div>
    </div>
  )
}

const EventCard = ({ event }: { event: any }) => {
  const { ambiance } = useCinematicsStore()
  const surface200 = ambiance?.palette?.surface?.[200]
  const surface200Rgba = hexToRgba(surface200, 0.5)
  const homeTeam = event?.homeTeam
  const awayTeam = event?.awayTeam
  const { url: homeTeamFlag } = useImage({
    id: homeTeam?.flag || '',
    cut: 'square',
    scale: '25'
  })
  const { url: awayTeamFlag } = useImage({
    id: awayTeam?.flag,
    cut: 'square',
    scale: '25'
  })

  const { date, isLive, isToday } = useEventDate({
    event: event,
    format: 'date-time'
  })

  return (
    <div
      className={classes.event}
      style={{
        background: surface200Rgba,
      }}
    >
      <div>
        <NextImage
          src={homeTeamFlag}
          alt={homeTeam?.name}
          width={48}
          height={48}
        />
      </div>
      <div className={classNames({
        [classes.date]: true,
        [classes.date__live]: isLive,
        [classes.date__today]: isToday
      })}>
        {date}
      </div>
      <div>
        <NextImage
          src={awayTeamFlag}
          alt={awayTeam?.name}
          width={48}
          height={48}
        />
      </div>
    </div>
  )
}